import { useCallback, useMemo, useReducer } from 'react';

export type Validator<T> = (value: T) => string | null;

interface FieldConfig<TValue> {
  initialValue: TValue;
  validators?: Array<Validator<TValue>>;
}

type FormConfig<TFields extends Record<string, unknown>> = {
  [Key in keyof TFields]: FieldConfig<TFields[Key]>;
};

interface FormState<TFields> {
  values: TFields;
  errors: Partial<Record<keyof TFields, string | null>>;
  touched: Partial<Record<keyof TFields, boolean>>;
}

type FormAction<TFields> =
  | { type: 'change'; field: keyof TFields; value: TFields[keyof TFields] }
  | { type: 'blur'; field: keyof TFields }
  | { type: 'setErrors'; errors: FormState<TFields>['errors']; touched: FormState<TFields>['touched'] }
  | { type: 'reset' };

function validateField<T>(value: T, validators: Array<Validator<T>> = []): string | null {
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return null;
}

export function useForm<TFields extends Record<string, unknown>>(config: FormConfig<TFields>) {
  const initialState: FormState<TFields> = useMemo(() => {
    const values = Object.keys(config).reduce((acc, key) => {
      const fieldKey = key as keyof TFields;
      acc[fieldKey] = config[fieldKey].initialValue;
      return acc;
    }, {} as TFields);

    return {
      values,
      errors: {},
      touched: {},
    };
  }, [config]);

  const [state, dispatch] = useReducer(
    (current: FormState<TFields>, action: FormAction<TFields>): FormState<TFields> => {
      switch (action.type) {
        case 'change': {
          const field = action.field;
          const value = action.value as TFields[keyof TFields];
          const validators = config[field].validators;
          const error = validateField(value, validators as Array<Validator<typeof value>>);
          return {
            values: { ...current.values, [field]: value },
            errors: { ...current.errors, [field]: error },
            touched: { ...current.touched, [field]: true },
          };
        }
        case 'blur': {
          const field = action.field;
          return {
            ...current,
            touched: { ...current.touched, [field]: true },
          };
        }
        case 'setErrors':
          return {
            ...current,
            errors: action.errors,
            touched: { ...current.touched, ...action.touched },
          };
        case 'reset':
          return initialState;
        default:
          return current;
      }
    },
    initialState
  );

  const handleChange = useCallback(
    <Field extends keyof TFields>(field: Field, value: TFields[Field]) => {
      dispatch({ type: 'change', field, value });
    },
    []
  );

  const handleBlur = useCallback(
    (field: keyof TFields) => {
      dispatch({ type: 'blur', field });
    },
    []
  );

  const handleSubmit = useCallback(
    (onValid: (values: TFields) => void) => (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const errors = Object.keys(config).reduce((acc, key) => {
        const field = key as keyof TFields;
        const value = state.values[field];
        const validators = config[field].validators as Array<Validator<typeof value>> | undefined;
        const error = validateField(value, validators);
        acc[field] = error;
        return acc;
      }, {} as FormState<TFields>['errors']);

      const touched = Object.keys(config).reduce((acc, key) => {
        acc[key as keyof TFields] = true;
        return acc;
      }, {} as FormState<TFields>['touched']);

      const hasErrors = Object.values(errors).some(Boolean);
      dispatch({ type: 'setErrors', errors, touched });

      if (!hasErrors) {
        onValid(state.values);
      }
    },
    [config, state.values]
  );

  const reset = useCallback(() => dispatch({ type: 'reset' }), []);

  return {
    values: state.values,
    errors: state.errors,
    touched: state.touched,
    handleChange,
    handleBlur,
    handleSubmit,
    reset,
  };
}

export const validators = {
  required: (message = 'Required'): Validator<unknown> => (value) =>
    value === null || value === undefined || value === '' ? message : null,
  minLength:
    (length: number, message = `Must be at least ${length} characters`): Validator<string> =>
    (value) => (value && value.length >= length ? null : message),
};
